import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Crown, Shield, UserCog, GraduationCap, ArrowLeft, Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCESS_TYPES = [
  {
    id: 'proprietaire',
    label: 'Accès Propriétaire',
    description: 'Super administrateur du compte — accès complet à la plateforme',
    icon: Crown,
    hoverColor: 'hover:border-amber-300 hover:bg-amber-50',
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    color: 'from-amber-500 to-orange-600',
    role: 'proprietaire',
    redirectTo: '/admin',
    needsPassword: true,
  },
  {
    id: 'administrateur',
    label: 'Accès Administrateur',
    description: "Personnel administratif avec droits d'administration",
    icon: Shield,
    hoverColor: 'hover:border-blue-300 hover:bg-blue-50',
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    color: 'from-blue-600 to-indigo-600',
    role: 'administrateur',
    redirectTo: '/admin',
    needsPassword: true,
  },
  {
    id: 'intervenant',
    label: 'Accès Intervenant',
    description: 'Formateurs et intervenants — droits définis par le propriétaire',
    icon: UserCog,
    hoverColor: 'hover:border-teal-300 hover:bg-teal-50',
    iconColor: 'text-teal-600',
    bgColor: 'bg-teal-50',
    color: 'from-teal-600 to-emerald-600',
    role: 'intervenant',
    redirectTo: '/admin',
    needsPassword: true,
  },
  {
    id: 'apprenant',
    label: 'Accès Apprenant',
    description: 'Espace apprenant — connexion avec votre email enregistré',
    icon: GraduationCap,
    hoverColor: 'hover:border-violet-300 hover:bg-violet-50',
    iconColor: 'text-violet-600',
    bgColor: 'bg-violet-50',
    color: 'from-violet-600 to-purple-600',
    role: 'apprenant',
    redirectTo: '/apprenant',
    needsPassword: false,
  },
];

const LoginPage = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !selectedAccess) return;
    if (selectedAccess.needsPassword && !password) {
      setError('Le mot de passe est requis');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const isLearner = selectedAccess.role === 'apprenant';
      const endpoint = isLearner ? '/api/auth/learner-login' : '/api/auth/role-login';
      const payload = isLearner
        ? { email: email.trim() }
        : { email: email.trim(), role: selectedAccess.role, password };

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMsg = 'Accès refusé';
        try {
          const cloned = response.clone();
          const parsed = await cloned.json();
          if (parsed.detail) errorMsg = parsed.detail;
        } catch {
          if (response.status === 401) errorMsg = 'Mot de passe incorrect';
          if (response.status === 403) errorMsg = "Accès refusé. Vérifiez vos droits d'accès.";
        }
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const userData = await response.clone().json();
      await checkAuth();
      toast.success(`Bienvenue, ${userData.name || userData.email}`);
      navigate(selectedAccess.redirectTo);
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess(false);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      if (res.ok) {
        setForgotSuccess(true);
      } else {
        const cloned = res.clone();
        try {
          const data = await cloned.json();
          setForgotError(data.detail || "Erreur lors de l'envoi");
        } catch {
          setForgotError("Erreur lors de l'envoi");
        }
      }
    } catch {
      setForgotError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <img src="/affea-logo.png" alt="AFFEA" className="w-14 h-14 rounded-lg object-cover" />
            <div>
              <h1 className="font-display text-2xl font-bold text-navy-700">AFFEA</h1>
              <p className="text-sm text-teal-600 italic">Académie Francophone de la Formation, de l'Enseignement et de l'Accompagnement</p>
            </div>
          </div>

          {/* Access type selection */}
          {!selectedAccess && (
            <>
              <h2 className="font-display text-3xl font-bold text-navy-700 mb-2 italic" data-testid="login-title">
                Bienvenue
              </h2>
              <p className="text-slate-600 mb-8">
                Choisissez votre type d'accès pour vous connecter.
              </p>

              <div className="space-y-3">
                {ACCESS_TYPES.map((access) => {
                  const Icon = access.icon;
                  return (
                    <button
                      key={access.id}
                      onClick={() => {
                        setSelectedAccess(access);
                        setEmail('');
                        setPassword('');
                        setError('');
                      }}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 transition-all duration-200 text-left group ${access.hoverColor}`}
                      data-testid={`access-btn-${access.id}`}
                    >
                      <div className={`w-12 h-12 rounded-xl ${access.bgColor} flex items-center justify-center shrink-0 transition-transform group-hover:scale-110`}>
                        <Icon className={`h-6 w-6 ${access.iconColor}`} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 text-sm">{access.label}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{access.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="mt-8 text-center text-xs text-slate-400">
                En vous connectant, vous acceptez nos{' '}
                <a href="#" className="text-teal-600 hover:underline">conditions d'utilisation</a>.
              </p>
            </>
          )}

          {/* Login form */}
          {selectedAccess && (
            <>
              <button
                onClick={() => { setSelectedAccess(null); setError(''); }}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
                data-testid="back-to-access"
              >
                <ArrowLeft className="h-4 w-4" /> Retour au choix d'accès
              </button>

              <div className={`flex items-center gap-3 mb-6 p-4 rounded-xl ${selectedAccess.bgColor}`}>
                <selectedAccess.icon className={`h-6 w-6 ${selectedAccess.iconColor}`} />
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">{selectedAccess.label}</h3>
                  <p className="text-xs text-slate-500">{selectedAccess.description}</p>
                </div>
              </div>

              <h2 className="font-display text-2xl font-bold text-navy-700 mb-2 italic" data-testid="email-form-title">
                Identifiez-vous
              </h2>
              <p className="text-slate-600 mb-6 text-sm">
                {selectedAccess.needsPassword
                  ? "Saisissez votre email et votre mot de passe."
                  : "Saisissez l'adresse email enregistrée lors de la création de votre compte apprenant."}
              </p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-sm text-slate-600">Adresse email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); setError(''); }}
                      placeholder="votre.email@exemple.com"
                      className="pl-10"
                      autoFocus
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                {selectedAccess.needsPassword && (
                  <div>
                    <Label htmlFor="login-password" className="text-sm text-slate-600">Mot de passe</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setError(''); }}
                        placeholder="Votre mot de passe"
                        className="pl-10 pr-10"
                        required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        data-testid="toggle-password"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100" data-testid="login-error">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className={`w-full bg-gradient-to-r ${selectedAccess.color} text-white py-6`}
                  disabled={loading || !email.trim() || (selectedAccess.needsPassword && !password)}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Connexion en cours...' : 'Se connecter'}
                </Button>
              </form>

              <p className="mt-6 text-center text-xs text-slate-400">
                {selectedAccess.needsPassword
                  ? (
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setForgotSuccess(false); setForgotError(''); }}
                      className="text-teal-600 hover:text-teal-700 hover:underline transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublié ?
                    </button>
                  )
                  : "Votre email doit être enregistré par votre organisme de formation."}
              </p>

              {/* Forgot Password Modal */}
              {showForgotPassword && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" data-testid="forgot-password-modal">
                    <div className="px-6 py-5 border-b border-slate-100">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-slate-900">Réinitialiser le mot de passe</h3>
                        <button
                          onClick={() => setShowForgotPassword(false)}
                          className="text-slate-400 hover:text-slate-600"
                          data-testid="close-forgot-modal"
                        >
                          <ArrowLeft className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="px-6 py-5">
                      {forgotSuccess ? (
                        <div className="text-center py-4" data-testid="forgot-success-message">
                          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Mail className="h-7 w-7 text-emerald-600" />
                          </div>
                          <h4 className="text-base font-semibold text-slate-900 mb-2">Email envoyé</h4>
                          <p className="text-sm text-slate-500 leading-relaxed">
                            Si un compte existe avec l'adresse <strong>{forgotEmail}</strong>,
                            vous recevrez un email avec un lien de réinitialisation.
                          </p>
                          <p className="text-xs text-slate-400 mt-3">Le lien est valable pendant 1 heure.</p>
                          <Button
                            className="mt-5"
                            onClick={() => setShowForgotPassword(false)}
                            data-testid="forgot-back-to-login"
                          >
                            Retour à la connexion
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={handleForgotPassword} className="space-y-4">
                          <p className="text-sm text-slate-500 leading-relaxed">
                            Saisissez l'adresse email associée à votre compte. Nous vous enverrons un lien pour réinitialiser votre mot de passe.
                          </p>
                          <div>
                            <Label htmlFor="forgot-email" className="text-sm text-slate-600">Adresse email</Label>
                            <div className="relative mt-1">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="forgot-email"
                                type="email"
                                value={forgotEmail}
                                onChange={e => { setForgotEmail(e.target.value); setForgotError(''); }}
                                placeholder="votre.email@exemple.com"
                                className="pl-10"
                                autoFocus
                                required
                                data-testid="forgot-email-input"
                              />
                            </div>
                          </div>
                          {forgotError && (
                            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100" data-testid="forgot-error">
                              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                              <p className="text-sm text-red-600">{forgotError}</p>
                            </div>
                          )}
                          <Button
                            type="submit"
                            className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 text-white"
                            disabled={forgotLoading || !forgotEmail.trim()}
                            data-testid="forgot-submit-btn"
                          >
                            {forgotLoading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
                          </Button>
                          <button
                            type="button"
                            onClick={() => setShowForgotPassword(false)}
                            className="w-full text-center text-sm text-slate-500 hover:text-slate-700"
                          >
                            Annuler
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right side - Branded panel */}
      <div className="hidden lg:flex flex-1 relative" style={{ background: 'linear-gradient(135deg, #0a3d62 0%, #0c2d48 40%, #38ada9 100%)' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20 z-10" />
        <div className="relative z-20 flex flex-col justify-center items-center p-16 w-full">
          <img src="/affea-logo.png" alt="AFFEA" className="w-28 h-28 rounded-2xl object-cover shadow-2xl mb-8" />
          <h2 className="font-display text-4xl font-bold text-white italic text-center mb-4">
            Plateforme d'Apprentissage
          </h2>
          <p className="text-white/80 text-center text-lg max-w-md mb-12">
            Développez vos compétences professionnelles avec AFFEA
          </p>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 max-w-lg">
            <blockquote className="text-white text-lg font-medium mb-4 leading-relaxed italic">
              "AFFEA m'a permis de transformer ma pratique professionnelle et d'acquérir
              des compétences essentielles pour mon évolution de carrière."
            </blockquote>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center">
                <span className="text-white font-bold">AN</span>
              </div>
              <div>
                <p className="text-white font-medium">Amos NGOUPAYOU</p>
                <p className="text-white/70 text-sm">Président, AFFEA</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
